<template>
  <div class="experience-container" :class="isPhone ? '' : 'px-5'">
    <!-- Header -->
    <div class="header-section">
      <p :class="isPhone ? 'merriweather-reg-20' : 'merriweather-reg-26'" class="mb-0 text-center">Nos</p>
      <p :class="isPhone ? 'merriweather-reg-28' : 'merriweather-reg-40'" class="mb-0 text-center">carnets d'adresses</p>
      <p class="text-center" :class="isPhone ? 'merriweather-light-18 mb-1' : 'merriweather-light-24 mb-1'">Notre sélection d'activité pour une <span :class="isPhone ? 'merriweather-light-italic-18' : 'merriweather-light-italic-24'">expérience locale authentique</span></p>
      <!-- "Voir sur la carte" link — mobile only -->
      <p v-if="isPhone" class="text-center mb-0 mt-1 d-flex align-items-start justify-content-center w-100">
        <img src="../../images/icons8-map-48.png" style="width: 21px; height: 21px;" alt="map icon" class="map-icon"></img>
        <a class="voir-carte-link raleway-reg-p15 ml-1" href="#" @click.prevent="openMapModal(null)">
          Voir sur la carte
        </a>
      </p>
    </div>

    <!-- Region Tabs -->
    <div class="tabs-section">
      <button
        class="tab-btn raleway-bold-p15 text-uppercase"
        v-for="region in regions"
        :key="region"
        :class="{ active: activeRegion === region }"
        @click="selectRegion(region)"
      >
        {{ region }}
      </button>
    </div>

    <!-- Main Content -->
    <div class="content-wrapper">
      <!-- Left Side - Experience Cards -->
      <div class="experiences-panel">
        <div v-if="loading" class="experiences-loading">
          <p class="raleway-light-italic-15">Chargement des expériences...</p>
        </div>
        <div v-else-if="error" class="experiences-error">
          <p class="raleway-light-italic-15">{{ error }}</p>
        </div>
        <div v-else-if="experiences.length === 0" class="experiences-empty">
          <p class="raleway-light-italic-15">Aucune expérience disponible pour cette région.</p>
        </div>
        <div v-else class="experiences-list">
          <div v-for="(experience, index) in filteredExperiences" :key="experience.id" class="experience-card">
            <div class="card-image">
              <img :src="experience.photo_key" :alt="experience.name" />
            </div>
            <div class="card-content">
              <h3 class="merriweather-reg-15 text-uppercase">{{ experience.name }}</h3>
              <p class="raleway-light-italic-15">{{ experience.catch_phrase }}</p>
              <div class="d-flex w-100 justify-content-between">
                <p class="raleway-light-italic-12">{{ experience.address }}</p>
                <div class="card-arrow" @click="focusExperience(experience)" title="Voir sur la carte">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Side - Inline Map (desktop only) -->
      <div class="map-panel" v-if="!isPhone">
        <div id="experience-destinations-map">
          <ExperienceMap
            :markers="mapMarkers"
            :lodgings="filteredExperiences"
            :currentLodging="syntheticLodging"
            :selectedExperience="focusedExperience"
            :selectCardMap="() => {}"
            :selectCard="() => {}"
          />
          <!-- Category Icon Filter at Bottom -->
          <div class="category-icon-wrapper">
            <ul class="icon-list">
              <li @click="filterExperiences('culturelles')"
                :class="activeCategories.includes('culturelles') ? 'li-active' : 'li-inactive'">
                <img src="https://conversions-api-gateway-9083319738648672-s3bucket-zfjcs6au7hlj.s3.eu-west-3.amazonaws.com/address_books/museum.svg" alt="culturelles" />
              </li>
              <li @click="filterExperiences('culinaires')"
                :class="activeCategories.includes('culinaires') ? 'li-active' : 'li-inactive'">
                <img src="https://conversions-api-gateway-9083319738648672-s3bucket-zfjcs6au7hlj.s3.eu-west-3.amazonaws.com/address_books/restaurant1.svg" alt="culinaires" />
              </li>
              <li @click="filterExperiences('au bord de l\'eau')"
                :class="activeCategories.includes('au bord de l\'eau') ? 'li-active' : 'li-inactive'">
                <img src="https://conversions-api-gateway-9083319738648672-s3bucket-zfjcs6au7hlj.s3.eu-west-3.amazonaws.com/address_books/restaurant1_group3.svg" alt="au bord de l'eau" />
              </li>
              <li @click="filterExperiences('au naturel')"
                :class="activeCategories.includes('au naturel') ? 'li-active' : 'li-inactive'">
                <img src="https://conversions-api-gateway-9083319738648672-s3bucket-zfjcs6au7hlj.s3.eu-west-3.amazonaws.com/address_books/leaves.svg" alt="au naturel" />
              </li>
              <li @click="filterExperiences('sportives')"
                :class="activeCategories.includes('sportives') ? 'li-active' : 'li-inactive'">
                <img src="https://conversions-api-gateway-9083319738648672-s3bucket-zfjcs6au7hlj.s3.eu-west-3.amazonaws.com/address_books/sports1.svg" alt="sportives" />
              </li>
              <li @click="filterExperiences('immersives')"
                :class="activeCategories.includes('immersives') ? 'li-active' : 'li-inactive'">
                <img src="https://conversions-api-gateway-9083319738648672-s3bucket-zfjcs6au7hlj.s3.eu-west-3.amazonaws.com/address_books/gamer1.svg" alt="immersives" />
              </li>
              <li @click="filterExperiences('DIY')"
                :class="activeCategories.includes('DIY') ? 'li-active' : 'li-inactive'">
                <img src="https://conversions-api-gateway-9083319738648672-s3bucket-zfjcs6au7hlj.s3.eu-west-3.amazonaws.com/address_books/diy.svg" alt="DIY" />
              </li>
              <li @click="filterExperiences('oenologiques')"
                :class="activeCategories.includes('oenologiques') ? 'li-active' : 'li-inactive'">
                <img src="https://conversions-api-gateway-9083319738648672-s3bucket-zfjcs6au7hlj.s3.eu-west-3.amazonaws.com/address_books/glass-with-wine.svg" alt="oenologiques" />
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile full-screen map modal -->
    <ExperienceMapModal
      v-if="showMapModal && syntheticLodging"
      :lodging="syntheticLodging"
      :visible="showMapModal"
      :focusedExperience="focusedExperience"
      @close="showMapModal = false"
    />
  </div>
</template>


<script>
import ExperienceMap from '../ExperienceMap.vue';
import ExperienceMapModal from '../ExperienceMapModal.vue';

export default {
  name: 'ExperienceCarousel',
  components: {
    ExperienceMap,
    ExperienceMapModal
  },
  props: {
    destinations: Array
  },
  data() {
    return {
      activeRegion: null,
      experiences: [],
      loading: false,
      error: null,
      showMapModal: false,
      focusedExperience: null,
      activeCategories: [],
    }
  },
  computed: {
    regions() {
      return this.destinations[0]?.regions?.map(region => region.name) || [];
    },
    isPhone() {
      return window.innerWidth <= 768;
    },
    // Get filtered experiences based on active categories
    filteredExperiences() {
      let filtered = this.experiences;
      
      // Filter by active categories if any are selected
      if (this.activeCategories.length > 0) {
        filtered = filtered.filter(exp => {
          const normalizedExpCategory = this.normalizeCategoryName(exp.category_name);
          return this.activeCategories.some(cat =>
            this.normalizeCategoryName(cat) === normalizedExpCategory
          );
        });
      }
      
      return filtered;
    },
    // Build marker array format expected by ExperienceMap: [longitude, latitude, id, category_name]
    mapMarkers() {
      return this.filteredExperiences
        .filter(exp => exp.longitude && exp.latitude)
        .map(exp => [exp.longitude, exp.latitude, exp.id, exp.category_name]);
    },
    // ExperienceMap/ExperienceMapModal require a lodging object with id, longitude, latitude.
    // We synthesize one from the first experience with coordinates.
    syntheticLodging() {
      const first = this.filteredExperiences.find(exp => exp.longitude && exp.latitude);
      if (!first) return null;
      return {
        id: first.id,
        longitude: first.longitude,
        latitude: first.latitude,
        name: this.activeRegion || 'Région',
        _synthetic: true, // tells ExperienceMap to skip nearby-lodgings API
      };
    }
  },
  mounted() {
    if (this.regions.length > 0) {
      this.activeRegion = this.regions[0];
      this.fetchExperiencesByRegion(this.activeRegion);
    }
  },
  methods: {
    selectRegion(region) {
      if (this.activeRegion === region) return;
      this.activeRegion = region;
      this.focusedExperience = null;
      this.activeCategories = [];
      this.fetchExperiencesByRegion(region);
    },
    async fetchExperiencesByRegion(regionName) {
      if (!regionName) return;
      this.loading = true;
      this.error = null;
      this.focusedExperience = null;
      try {
        const response = await fetch(
          `/experiences/by_region?region=${encodeURIComponent(regionName)}`,
          { headers: { 'Accept': 'application/json' } }
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        this.experiences = await response.json();
      } catch (err) {
        console.error('Failed to fetch experiences:', err);
        this.error = 'Impossible de charger les expériences.';
        this.experiences = [];
      } finally {
        this.loading = false;
      }
    },
    // Called when the arrow icon on a card is clicked
    focusExperience(experience) {
      this.focusedExperience = experience;
      if (this.isPhone) {
        this.showMapModal = true;
      }
      // On desktop, the ExperienceMap will react to the updated focusedExperience/selectedExperience prop
    },
    // Called from "Voir sur la carte" link — opens modal for all experiences (no focused one)
    openMapModal(experience) {
      this.focusedExperience = experience || null;
      this.showMapModal = true;
    },
    // Filter experiences by category
    filterExperiences(selectedCategory) {
      if (this.activeCategories.includes(selectedCategory)) {
        this.activeCategories = [];
      } else {
        this.activeCategories = [selectedCategory];
      }
    },
    // Normalize category name for comparison
    normalizeCategoryName(categoryName) {
      if (!categoryName) return '';
      return categoryName.toLowerCase().trim();
    }
  }
}
</script>

<style scoped>
.experience-container {
  width: 100%;
}

.voir-carte-link {
  color: #3B6152;
  text-decoration: underline;
  cursor: pointer;
}

.voir-carte-link:hover {
  color: #2a4a3d;
}

/* Tabs */
.tabs-section {
  display: flex;
  gap: 0;
  padding: 30px 40px;
  overflow: auto;
  max-width: 98%;
  white-space: nowrap;
}

.tab-btn {
  background: none;
  border: none;
  padding: 12px 20px;
  letter-spacing: 1px;
  text-transform: uppercase;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  color: #524F4FDD;
}

.tab-btn:hover {
  color: #000;
}

.tab-btn.active {
  color: #000000;
  border-bottom: 2px solid #3B6152;
}

/* Main Content */
.content-wrapper {
  display: flex;
  height: calc(100vh - 300px);
  min-height: 600px;
}

.experiences-panel {
  flex: 1;
  overflow-y: auto;
  padding: 40px;
  position: relative;
}

.experiences-list {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.experience-card {
  display: flex;
  gap: 10px;
  padding: 20px;
  background: #F0F7F4 0% 0% no-repeat padding-box;
  border: 2px solid #FFFFFF;
  cursor: pointer;
}

.card-image {
  flex-shrink: 0;
  width: 144px;
  height: 104px;
  overflow: hidden;
  background: var(--light-bg);

  @media screen and (max-width: 767px) {
    width: 105px;
    height: 144px;
  }
}

.card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.card-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
  padding-right: 30px;
}

.card-arrow {
  width: 30px;
  height: 30px;
  cursor: pointer;
  color: #3B6152;
  transition: transform 0.2s ease, color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.card-arrow:hover {
  transform: translateX(4px);
  color: #2a4a3d;
}

/* Map Panel */
.map-panel {
  flex: 1;
  display: flex;
  align-items: stretch;
  position: relative;
  overflow: hidden;
}

#experience-destinations-map {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* Override ExperienceMap internal sizing to fill panel */
#experience-destinations-map #map-test {
  flex: 1;
  height: 100% !important;
}

.map-placeholder {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  background: linear-gradient(135deg, #e8e6e1 0%, #ddd9d0 100%);
}

/* Scrollbar Styling */
.experiences-panel::-webkit-scrollbar {
  width: 8px;
}

.experiences-panel::-webkit-scrollbar-track {
  background: transparent;
}

.experiences-panel::-webkit-scrollbar-thumb {
  background: #3B6152;
  border-radius: 4px;
}

/* Category Icon Wrapper */
.category-icon-wrapper {
  background: #efefef;
  padding: 5px;
  display: flex;
  justify-content: center;
  border-top: 1px solid #ddd;
}

ul.icon-list {
  display: flex;
  justify-content: space-around;
  gap: 0.5rem;
  padding-left: 0;
  margin: 0;
}

ul.icon-list li {
  list-style: none;
  background: #fff;
  padding: 0.5rem 1.5rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

ul.icon-list li.li-active {
  background: #3b6152;
}

ul.icon-list li img {
  width: 100%;
  max-width: 74 px;
  height: auto;
  cursor: pointer;
}

.li-active img {
  filter: brightness(0) invert(1);
}

.li-inactive img {
  filter: invert(0);
}

/* Responsive */
@media (max-width: 1024px) {
  .content-wrapper {
    flex-direction: column;
    height: auto;
  }

  .experiences-panel {
    border-right: none;
    max-height: 500px;
  }

  .tab-btn {
    padding: 10px 15px;
  }
}

@media (max-width: 768px) {
  .header-section {
    padding: 40px 20px 30px;
  }

  .experiences-panel {
    padding: 25px;
  }

  .experience-card {
    padding: 15px;
  }

  .card-content {
    padding-right: 0;
  }

  .tabs-section {
    padding: 20px 20px;
    gap: 8px;
  }

  .tab-btn {
    padding: 8px 12px;
  }
  .content-wrapper {
    min-height: unset;
  }
}
</style>

<style>
/* Non-scoped: override ExperienceMap map height inside our panel */
#experience-destinations-map #map-test #map,
#experience-destinations-map .mapboxgl-map {
  height: 100% !important;
  min-height: 500px;
}
</style>